import { ref, watch } from 'vue'
import type { Ref } from 'vue'
import type { BlogSearchSeoPayload } from '~/types/BlogSearchHub'
import type { UseMetiersSearchHubReturn } from '~/types/MetiersSearchHub'
import type { ProfessionCardObj } from '~/types/profession'
import {
  getMockMetiersSearchProfessions,
  type MetiersSearchProfessionRow,
} from '~/composables/metiersSearchMockData'

const PUBLIC_METIERS_TOTAL: number = 4006

/**
 * Projette une ligne mock (filtres internes) vers `ProfessionCardObj`.
 * @param {MetiersSearchProfessionRow} row Ligne avec `domainValue` / `sectorValue`.
 * @returns {ProfessionCardObj} Objet carte métier pour `UiProfessionCard`.
 */
function toProfessionCardObj(row: MetiersSearchProfessionRow): ProfessionCardObj {
  const { domainValue: _d, sectorValue: _s, ...card } = row
  return card
}

/**
 * État et logique pour la page recherche métiers (`/recherche/metiers`) : SEO via `GET /api/aca/seointer`
 * (`pageType=professions`), grille mock filtrée par texte, domaine et secteur.
 * @returns {UseMetiersSearchHubReturn} Réactifs, total affiché et fonction `filterProfessions`.
 */
export function useMetiersSearchHub(): UseMetiersSearchHubReturn {
  const { locale } = useI18n()

  const seoPayload: Ref<BlogSearchSeoPayload | null> = ref<BlogSearchSeoPayload | null>(null)
  const loading: Ref<boolean> = ref<boolean>(false)
  const error: Ref<string | null> = ref<string | null>(null)

  /**
   * Charge le bloc SEO hub métiers (sans `param1`).
   * @returns {Promise<void>}
   */
  async function fetchSeo(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const { data } = await useFrontApi().get<BlogSearchSeoPayload>('/api/aca/seointer', {
        params: { language: locale.value, pageType: 'professions' },
      })
      seoPayload.value = data
    } catch (e: unknown) {
      const err: Error = e instanceof Error ? e : new Error(String(e))
      error.value = err.message ?? 'Erreur réseau'
      seoPayload.value = null
    } finally {
      loading.value = false
    }
  }

  watch(
    (): string => locale.value,
    (): void => {
      void fetchSeo()
    },
    { immediate: true },
  )

  const allProfessions: Ref<MetiersSearchProfessionRow[]> = ref<MetiersSearchProfessionRow[]>(
    getMockMetiersSearchProfessions(),
  )

  function filterProfessions(
    searchQuery: string,
    domain: string,
    sector: string,
  ): ProfessionCardObj[] {
    const q: string = searchQuery.trim().toLowerCase()
    const d: string = domain.trim()
    const s: string = sector.trim()
    let list: MetiersSearchProfessionRow[] = allProfessions.value.filter(
      (row: MetiersSearchProfessionRow) => {
        if (d.length > 0 && row.domainValue !== d) return false
        if (s.length > 0 && row.sectorValue !== s) return false
        return true
      },
    )
    if (q.length > 0) {
      list = list.filter(
        (row: MetiersSearchProfessionRow) =>
          row.Name.toLowerCase().includes(q) ||
          row.Description.toLowerCase().includes(q) ||
          row.Domain.toLowerCase().includes(q) ||
          row.Sector.toLowerCase().includes(q),
      )
    }
    return list.map(toProfessionCardObj)
  }

  return {
    seoPayload,
    loading,
    error,
    publicTotalCount: PUBLIC_METIERS_TOTAL,
    filterProfessions,
  }
}
